import { useState } from "react";
import {
  Box,
  Button,
  Card,
  Typography,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Loading from "../../components/loading/loading";
import ErrorNotification from "../../components/error-notification/error-notification";
import Notification from "../../components/notification/notification";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  passwordRegex,
  lowercaseRegex,
  uppercaseRegex,
  specialCharRegex,
} from "../../utils/Constant";
import { resetPasswordWithTokenRequest } from "../../utils/APICalls";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const { token } = useParams();
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [lengthOk, setLengthOk] = useState(false);
  const [lowercaseOk, setLowercaseOk] = useState(false);
  const [uppercaseOk, setUppercaseOk] = useState(false);
  const [specialOk, setSpecialOk] = useState(false);
  const [matchOk, setMatchOk] = useState(false);

  const validationSchema = Yup.object({
    newPassword: Yup.string()
      .required("This field can't be empty")
      .matches(passwordRegex, "Error"),
    confirmPassword: Yup.string()
      .required("This field can't be empty")
      .oneOf([Yup.ref("newPassword")], "Passwords must match"),
  });

  const formik = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setShowLoading(true);
        const response = await resetPasswordWithTokenRequest({
          resetToken: token as string,
          password: values.newPassword,
        });
        if (
          response?.status === 200 ||
          response?.status === 201 ||
          response?.status === 204
        ) {
          setShowSuccessNotification(true);
          setSuccessMessage("Password has been reset successfully.");
          setTimeout(() => navigate("/signin"), 1200);
        } else {
          setShowErrorNotification(true);
          setErrorMessage(
            (response && (response as any).data?.detail) ||
              "Failed to reset password."
          );
        }
      } catch (e) {
        setShowErrorNotification(true);
        setErrorMessage("Unexpected error. Please try again.");
      } finally {
        setShowLoading(false);
      }
    },
  });

  const onNewPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(event);
    const pwd = event.target.value || "";
    setLengthOk(pwd.length >= 8 && pwd.length <= 16);
    setLowercaseOk(lowercaseRegex.test(pwd));
    setUppercaseOk(uppercaseRegex.test(pwd));
    setSpecialOk(specialCharRegex.test(pwd));
    setMatchOk(pwd !== "" && pwd === formik.values.confirmPassword);
  };

  const onConfirmPasswordChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    formik.handleChange(event);
    const val = event.target.value || "";
    setMatchOk(val !== "" && val === formik.values.newPassword);
  };

  return (
    <Box className="backgroud-layout">
      <Box className="signin-layout">
        <Card className="signin-card" sx={{ p: 2 }}>
          <img
            className="signin-logo"
            src={require("../../assets/Vision_Logo.svg").default}
            alt="logo"
          />
          <Typography
            className="signin-text"
            variant="h5"
            sx={{ margin: "8px 0" }}
          >
            Set a New Password
          </Typography>
          <FormControl sx={{ mt: 1, width: 500 }} variant="outlined">
            <InputLabel htmlFor="newPassword">New Password</InputLabel>
            <OutlinedInput
              id="newPassword"
              name="newPassword"
              type={showNewPassword ? "text" : "password"}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      showNewPassword
                        ? "hide the password"
                        : "display the password"
                    }
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseUp={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showNewPassword ? (
                      <VisibilityOutlinedIcon />
                    ) : (
                      <VisibilityOffOutlinedIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              }
              label="New Password"
              value={formik.values.newPassword}
              onChange={onNewPasswordChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.newPassword && Boolean(formik.errors.newPassword)
              }
            />
          </FormControl>

          <Box sx={{ m: 1 }}>
            <Typography
              sx={{ fontSize: 12, display: "flex", alignItems: "center" }}
              color={lengthOk ? "success.main" : "text.secondary"}
            >
              {lengthOk && (
                <CheckCircleOutlinedIcon sx={{ mr: 0.75 }} color="success" />
              )}
              8-16 characters
            </Typography>
            <Typography
              sx={{ fontSize: 12, display: "flex", alignItems: "center" }}
              color={lowercaseOk ? "success.main" : "text.secondary"}
            >
              {lowercaseOk && (
                <CheckCircleOutlinedIcon sx={{ mr: 0.75 }} color="success" />
              )}
              At least 1 lowercase
            </Typography>
            <Typography
              sx={{ fontSize: 12, display: "flex", alignItems: "center" }}
              color={uppercaseOk ? "success.main" : "text.secondary"}
            >
              {uppercaseOk && (
                <CheckCircleOutlinedIcon sx={{ mr: 0.75 }} color="success" />
              )}
              At least 1 uppercase
            </Typography>
            <Typography
              sx={{ fontSize: 12, display: "flex", alignItems: "center" }}
              color={specialOk ? "success.main" : "text.secondary"}
            >
              {specialOk && (
                <CheckCircleOutlinedIcon sx={{ mr: 0.75 }} color="success" />
              )}
              Special character: ! @ # $ % ^ & *
            </Typography>
          </Box>

          <FormControl sx={{ mt: 2, width: 500 }} variant="outlined">
            <InputLabel htmlFor="confirmPassword">
              Confirm New Password
            </InputLabel>
            <OutlinedInput
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      showConfirmPassword
                        ? "hide the password"
                        : "display the password"
                    }
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseUp={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showConfirmPassword ? (
                      <VisibilityOutlinedIcon />
                    ) : (
                      <VisibilityOffOutlinedIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              }
              label="Confirm New Password"
              value={formik.values.confirmPassword}
              onChange={onConfirmPasswordChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.confirmPassword &&
                Boolean(formik.errors.confirmPassword)
              }
            />
          </FormControl>
          <Typography
            sx={{
              fontSize: 12,
              m: 1,
              display: "flex",
              alignItems: "center",
            }}
            color={
              matchOk
                ? "success.main"
                : formik.values.confirmPassword.length > 0
                ? "error"
                : "text.secondary"
            }
          >
            {matchOk && (
              <CheckCircleOutlinedIcon sx={{ mr: 0.75 }} color="success" />
            )}
            Passwords Must Match
          </Typography>
          <Button
            disabled={showLoading}
            sx={{ m: 1.5, width: 500 }}
            variant="contained"
            onClick={() => formik.handleSubmit()}
          >
            Set New Password
          </Button>
        </Card>
      </Box>
      {showLoading && <Loading />}
      <ErrorNotification
        show={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />
      <Notification
        show={showSuccessNotification}
        message={successMessage}
        onClose={() => setShowSuccessNotification(false)}
      />
    </Box>
  );
}
